import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react';
import { Carousel, message } from 'antd';
import folderBg from '@/assets/images/folder_bg.jpg';
import type { IGridItem, TItem } from '@/components/GridBoards/types';
import { useLayoutFolder, useMenu } from '@/store';
import APP_ITEMS from '../items/apps';
import GridMain from '@/components/GridBoards/GridMain';
import { $fm, $t } from '@/Intl';
import createApp from './createApp';
import createThirdApp from './createThirdApp';
import { THIRD_APP_ICON_CACHE } from '@/utils/THIRD_APP_ICON_CACHE';
import { deepClone, evenlySplit } from '@/utils';
import { getArea } from '../doWithSider';
import AppImg from '@/components/AppImg';

// 文件夹弹窗

interface FolderModalProps {
  onMaskClose: () => void;
  i: number;
  maskWidth: number | undefined;
  maskHeight: number | undefined;
  gm: GridMain; // gridmain 实例
  fid: string;
  siderbounds: LayoutBoxSiderbounds;
}
const CurFolderModal: FC<FolderModalProps> = observer(
  ({ onMaskClose, i, maskWidth, maskHeight, gm, fid, siderbounds }) => {
    const [folder = [], updateLayoutFolder] = useLayoutFolder();
    const [{ thirdApp = [] }] = useMenu();
    const cloneFolder = deepClone(folder);
    const curf = folder[i];
    const pages = evenlySplit(curf?.child || [], 4);
    const handleMouseDown = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.target === e.currentTarget) {
        onMaskClose();
      }
    };

    const handleChangeTitle = (v: string) => {
      cloneFolder[i].title = v;
      gm.itemAttrs[fid].title = v;
      updateLayoutFolder(cloneFolder);
    };

    const handleDel = (_class: string, title?: string) => {
      // 如果不是内置app， 则说明是三方app
      const tmp = (APP_ITEMS as any)[_class]
        ? createApp({ className: _class }, siderbounds)
        : createThirdApp(
            {
              className: _class,
              title: title || '',
            },
            siderbounds,
          );
      const isavailable = gm.getEmptyPostion(tmp);
      if (isavailable) {
        gm.itemAttrs[fid].child = gm.itemAttrs[fid].child?.filter(
          ({ className }) => className !== _class,
        );
        gm.manualInsertItem(tmp);
        return true;
      } else {
        message.error($t('NOSPACE_WARN'));
        return false;
      }
    };

    return createPortal(
      <div
        className='layout-box-folder-details'
        onMouseDown={handleMouseDown}
        style={{ width: maskWidth, height: maskHeight }}
      >
        <input
          className='folder-title'
          value={curf.title}
          onChange={(e) => {
            handleChangeTitle(e.target.value);
          }}
          maxLength={20}
        />
        <div className='folder-child'>
          <Carousel dots={{ className: 'folder-toggle-dots' }}>
            {pages.map((childs, i) => {
              return (
                <div key={i} className='folder-app-icon-wrapper'>
                  {childs.map(({ className }) => {
                    const id = className as keyof typeof APP_ITEMS;
                    let img = '';
                    let title: any = '';
                    if (APP_ITEMS[id]) {
                      img = APP_ITEMS[id].img;
                      title = $fm(APP_ITEMS[id].title);
                    } else {
                      const mta = thirdApp.find((tha) => tha.className === id);
                      if (mta) {
                        img = THIRD_APP_ICON_CACHE[className];
                        title = mta.displayName;
                      }
                    }
                    return (
                      <div className='folder-app-icon' key={className}>
                        <i onClick={() => handleDel(className, title)}>×</i>
                        <AppImg src={img} alt={className} />
                        <span>{title}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </Carousel>
        </div>
      </div>,
      gm.mainRef.current!.parentElement!,
    );
  },
);

// 文件夹
interface FolderItemProps {
  item: IGridItem;
  screen: number;
  gm: GridMain;
  siderbounds: LayoutBoxSiderbounds;
}

const FolderItem: FC<FolderItemProps> = observer(
  ({ item, screen, gm, siderbounds }) => {
    const [folder = []] = useLayoutFolder();
    const [opened, setOpened] = useState(false);
    const i = folder.findIndex(
      (f) => f.screen === screen && f.x === item.x && f.y === item.y,
    );
    const curf = folder[i];
    const child = curf?.child || [];

    return (
      <div
        className='layout-folder'
        onDoubleClick={(e) => {
          setOpened(true);
        }}
      >
        <img src={folderBg} alt='folder' />
        <div className='layout-folder-child'>
          {child.slice(0, 4).map((c) => {
            const imgsrc =
              APP_ITEMS[c.className as keyof typeof APP_ITEMS]?.img ||
              THIRD_APP_ICON_CACHE[c.className];
            return <AppImg src={imgsrc} alt={c.className} key={c.className} />;
          })}
        </div>
        <span>{curf?.title || $t('UNNAMED_FOLDER')}</span>
        {opened ? (
          <CurFolderModal
            onMaskClose={() => setOpened(false)}
            maskHeight={gm.props.height}
            maskWidth={gm.props.width}
            i={i}
            gm={gm}
            fid={item.id}
            siderbounds={siderbounds}
          />
        ) : null}
      </div>
    );
  },
);

const createFolder = (
  props: {
    title: string;
    screen: number;
    x?: number;
    y?: number;
    child: any[];
  },
  siderbounds: LayoutBoxSiderbounds,
): TItem => {
  return {
    id: 'folder',
    title: props.title || $t('UNNAMED_FOLDER'),
    child: props.child || [],
    x: props.x ?? -1,
    y: props.y ?? -1,
    w: 1,
    h: 1,
    isFolder: true,
    area: getArea(siderbounds),
    render: (item, _, __, gm) => {
      return (
        <FolderItem
          item={item}
          screen={props.screen}
          gm={gm}
          siderbounds={siderbounds}
        />
      );
    },
  };
};

export default createFolder;
